<template>
  <div
    class="gift-reggie-response"
  >
    <h6 class="gift-reggie-response__title">
      {{ message }}
    </h6>

    <ul
      v-show="responseType === 'selectRegistry'"
      class="body-2 gift-reggie-response__registry-list"
    >
      <li
        v-show="loadingRegistries"
        v-html="$icon('misc.loading')"
      />

      <li
        v-for="registry in registries"
        v-show="!loadingRegistries"
        :key="registry.id"
        class="gift-reggie-response__registry-list-item"
      >
        <button
          class="gift-reggie-response__registry-button"
          type="button"
          @click.prevent="handleResponseClick($event, registry.id)"
        >
          <span
            :aria-label="$string('wishlist.modal_actions.registry')"
            class="input-checkbox"
          />
          {{ registry.name }}
        </button>
      </li>
    </ul>

    <btn
      :icon="false"
      :label="actionLabel"
      :state="(removingWishlist && responseType === 'remove') || loading"
      class="gift-reggie-response__action"
      modifiers="text center"
      state-icon="misc.loading"
      @click.prevent="handleResponseClick"
    />
  </div>
</template>

<script>

/**
 * Vue: Gift reggie response (gift-reggie-response)
 * -----------------------------------------------------------------------------
 * Various modal states that appear depending on the response from the user
 * interaction with the wishlist modal.
 *
 * @param {String} actionType - 'wishlist' or 'registry'
 * @param {Boolean} removingWishlist - Show loading circle while item is
 * being removed from wishlist.
 * @param {String} responseType - Can be one of the following: 'success',
 * 'remove', 'login', or 'selectRegistry'
 * - 'success' will display a successful add message and will display a link
 * to the selected actionType ('wishlist' or 'registry').
 * - 'remove' will display an option to remove the variant from the wishlist.
 * - 'login' will prompt the user to log in.
 * - 'selectRegistry' will display a list of registries that the user can add
 * the product to and a link to create a new registry.
 * @param {Object} session - The gift reggie session when user is logged in.
 */

export default {
  name: 'GiftReggieResponse',

  props: {
    actionType: {
      type: String,
      default: '',
    },
    productId: {
      type: Number,
      default: null,
    },
    removingWishlist: {
      type: Boolean,
      default: false,
    },
    responseType: {
      type: String,
      default: '',
    },
    session: {
      type: Object,
      default: () => ({}),
    },
    variantId: {
      type: Number,
      default: null,
    },
  },

  emits: ['wishlist-remove', 'registry-update'],

  data() {
    return {
      loading: false,
      loadingRegistries: false,
      registries: [],
      registryId: '',
      params: new URLSearchParams(),
    }
  },

  computed: {

    /**
     * Compute the text for the pink text-link action at the bottom of the
     * response states.
     * @returns {string}
     */
    actionLabel() {
      if (!this.responseType) {
        return ''
      }

      const listName = this.$string(`wishlist.modal_actions.${this.actionType}`)
      const options = { replace: { list_name: listName } }
      let action = this.responseType

      if (this.responseType === 'success') {
        action = 'view'
      }
      else if (this.responseType === 'selectRegistry') {
        action = 'create_registry'
      }

      return this.$string(`wishlist.modal_actions.${action}`, options)
    },

    /**
     * Compute the response message.
     * @returns {string}
     */
    message() {
      if (!this.actionType) {
        return ''
      }

      const listName = this.$string(`wishlist.modal_actions.${this.actionType}`)
      const options = { replace: { list_name: listName } }
      let str = ''

      switch (this.responseType) {
        case 'success':
          str = this.$string('wishlist.modal.success', options)
          break
        case 'remove':
          str = this.$string('wishlist.modal.item_in_wishlist')
          break
        case 'login':
          str = this.$string('wishlist.modal.login', options)
          break
        case 'selectRegistry':
          str = this.$string('wishlist.modal.select_registry')
          break
      }

      return str
    },
  },

  watch: {
    actionType() {
      this.params.set('actionType', this.actionType)
    },

    productId() {
      this.params.set('product', this.productId)
    },

    variantId() {
      this.params.set('variant', this.variantId)
    },
  },

  mounted() {
    this.params.set('checkout_url', encodeURI((window.location.pathname)))
    this.params.set('wishlist', this.actionType)

    if (this.session?.sessionId) {
      this.params.set('sid', this.session.sessionId)
    }

    if (this.productId) {
      this.params.set('product', this.productId)
    }

    if (this.variantId) {
      this.params.set('variant', this.variantId)
    }

    this.getRegistries()
  },

  methods: {

    /**
     * Get the user's registries.
     */
    getRegistries() {
      if (!this.session) {
        return
      }

      this.loadingRegistries = true

      this.session.getRegistries()
        .done(payload => {
          this.registries = payload.registries
          this.loadingRegistries = false
        })
        .fail(
          error => console.error('[GiftReggie - getRegistries]', error),
        )
    },

    /**
     * Handle a series of actions in response to the user pressing the
     * text-link action or to the user selecting a registry.
     * @param event
     * @param registryId
     */
    handleResponseClick(event, registryId) {
      const params = this.params
      let url = ''

      switch (this.responseType) {
        case 'success':

          /**
           * Direct the user to the wishlist or registry page where the user
           * added an item.
           */
          if (this.actionType === 'wishlist') {
            url = this.$variable('routes.giftReggie.wishlist')
            window.location = url
          }
          else {
            url = this.$variable('routes.giftReggie.registry')
            window.location = `${url}${this.registryId}?${params.toString()}`
          }
          break
        case 'remove':

          /**
           * Tell the parent component to remove the selected variant from
           * wishlist.
           */
          this.$emit('wishlist-remove')
          break
        case 'login':

          /**
           * Direct the user to the log in page.
           */
          url = this.$variable('routes.account.login')
          window.location = `${url}?${params.toString()}`
          break
        case 'selectRegistry':

          /**
           * Tell the parent component which registry was selected, or direct
           * the user to create a registry page if the text-link action was
           * clicked.
           */
          if (registryId) {
            this.loadingRegistries = true
            this.registryId = registryId
            this.$emit('registry-update', registryId)
          }
          else {
            url = this.$variable('routes.giftReggie.create')
            window.location = `${url}?${params.toString()}`
          }
          break
      }

      if (url) {
        this.loading = true
      }
    },
  },
}
</script>

<style lang="scss">
@import './gift-reggie-response';
</style>
